import { useState, useEffect, useRef } from 'react';

const TYPE_SPEED = 50;
const PAUSE_BETWEEN = 800;

export interface UseAuthPhaseOptions {
  /** System messages to type sequentially. Last message stays displayed. */
  systemMessages: string[];
}

/**
 * Auth scene phase orchestration hook.
 *
 * Controls 5-phase entrance animation and sequential system text typing.
 * - Phase 0→5: timed reveal of scene elements (background, panel, header, form)
 * - System text: types each message in `systemMessages` sequentially;
 *   pauses between messages, last message remains displayed.
 */
export function useAuthPhase({ systemMessages }: UseAuthPhaseOptions) {
  const [phase, setPhase] = useState(0);
  const [systemText, setSystemText] = useState('');
  const cancelled = useRef(false);

  // Phase progression (0 → 5)
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setPhase(1), 100));
    timers.push(setTimeout(() => setPhase(5), 200));
    return () => timers.forEach(clearTimeout);
  }, []);

  // Sequential message typing (starts at phase 3)
  useEffect(() => {
    if (phase < 1 || systemMessages.length === 0) return;
    cancelled.current = false;

    let timeoutId: ReturnType<typeof setTimeout>;

    const typeMessage = (msgIndex: number, charIndex: number) => {
      if (cancelled.current) return;
      if (msgIndex >= systemMessages.length) return;

      const msg = systemMessages[msgIndex] ?? '';

      if (charIndex <= msg.length) {
        setSystemText(msg.slice(0, charIndex));
        timeoutId = setTimeout(() => typeMessage(msgIndex, charIndex + 1), TYPE_SPEED);
      } else if (msgIndex < systemMessages.length - 1) {
        // Pause then move to next message
        timeoutId = setTimeout(() => {
          if (cancelled.current) return;
          setSystemText('');
          timeoutId = setTimeout(() => typeMessage(msgIndex + 1, 0), 150);
        }, PAUSE_BETWEEN);
      }
      // Last message stays displayed
    };

    typeMessage(0, 0);

    return () => {
      cancelled.current = true;
      clearTimeout(timeoutId);
    };
  }, [phase, systemMessages]);

  return { phase, systemText };
}
